import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import moment from "moment";

import { userStyles } from "./Styles/PlolygonServicesPageStyles";
import { ALERT_TYPES, API_RESPONSE_STATUS, CoordinatesInterface, SLOT_STATUSES } from "../../config/constants";

import * as PlolygonServicesInterface from "./Utils/PlolygonServicesInterface";
import * as PlolygonServicesSlice from "./Redux/PlolygonServicesSlice";
import * as PlolygonServicesUtils from "./Utils/PlolygonServicesUtils";
import * as PlolygonServicesConstants from "./Utils/PlolygonServicesConstants";

import Loader from "../common/Loader/Loader";
import PlolygonServicesHeader from "./Common/PlolygonServicesHeader";
import PlolygonServicesCardSection from "./Common/PlolygonServicesCardSection";
import CustomAlert from "../common/CustomAlert/CustomAlert";
import PolygonServiceFilterSection from "./Common/PolygonServiceFilterSection";
import PolygonMapService from "./PolygonMapService/PolygonMapService";
import OverlayLoader from "../common/Loader/OverlayLoader";

import { selectUserDetails } from "../Login/redux/userSlice";
import { downloadXLSXFile } from "utils/helperFunctions";
import { isArrayValid, isAString, isEmptyArray, isEmptyOrNull, isUndefined } from "../../utils/DataUtils";
import { PolygonPosZoneInfoWindowViewUtils } from "../common/App/AppViewUtils";
import { serviceSelectPropositionRecord } from "../../config/redux/configurationsSlice";
import { DateFormat, getMomentDateBasedOnFormat, getTodaysDayInString, isDateValid } from "../../utils/DateUtils";
import RenderPolygonDetailsPopUp from "./Common/RenderPolygonDetailsPopUp";

const PlolygonServicesPage = () => {
  const classes = userStyles();
  const appDispatch = useDispatch();

  const { currentCountry } = useSelector((state: any) => state.appConfig);
  const { countryDetails } = useSelector((state: any) => state.countrySettings);
  const { polygonServiceMapRecord } = useSelector((state: any) => state.plolygonService);
  const polygonServiceConfigurationRecord = useSelector(PlolygonServicesSlice.selectPolygonServiceConfigurationRecord);
  const userInfo: any = useSelector(selectUserDetails);
  const servicePropositionList = useSelector(serviceSelectPropositionRecord);

  const [appCountStateObj, setAppCountStateObj] = useState<PlolygonServicesInterface.PlolygonServicesInitialCountStateInteface>({ ...PlolygonServicesInterface.PlolygonServicesInitialCountState });
  const [appStateObj, setAppStateObj] = useState<PlolygonServicesInterface.PlolygonServicesInitialStateInteface>({ ...PlolygonServicesInterface.PlolygonServicesInitialState });
  const [appFilterPopUpObj, setAppFilterPopUpObj] = useState<PlolygonServicesInterface.PolygonFilterPopUpStateInterface>({ ...PlolygonServicesInterface.PolygonFilterPopUpInitialState });
  const [polygonDetailsSideDrawerObj, setPolygonDetailsSideDrawerObj] = useState<PlolygonServicesInterface.PolygonDetailsSideDrawerInterface>({ ...PlolygonServicesInterface.PolygonDetailsSideDrawerInitialState });

  // Ref mirror of appCountStateObj.selectedServiceTypes so handlers (e.g. dropdown close) can read
  // the current selection synchronously instead of risking a stale closure.
  const selectedServiceTypesRef = useRef<Array<string>>(appCountStateObj.selectedServiceTypes);
  useEffect(() => {
    selectedServiceTypesRef.current = appCountStateObj.selectedServiceTypes;
  }, [appCountStateObj.selectedServiceTypes]);

  // Listing view: which service types to fetch with. Prefers the user's current column
  // selection; before that's populated (the very first fetch on mount, ahead of the
  // "compute defaults" effect below), recomputes the same default-first-3-by-rank set directly
  // instead of falling back to the full permitted list.
  const getEffectiveServiceTypesForFetch = (): string[] => {
    if (isArrayValid(appCountStateObj.selectedServiceTypes)) {
      return [...appCountStateObj.selectedServiceTypes];
    }
    const defaultServiceTypeList = PlolygonServicesUtils.generateServiceNameValueForPolygon(servicePropositionList, userInfo);
    if (isArrayValid(defaultServiceTypeList)) {
      return defaultServiceTypeList.slice(0, 3).map((service: any) => service.id);
    }
    return [];
  };

  // Fetch Record
  //
  const fetchPolygonRecord = (countryName: any) => {
    const serviceTypeFilterList = getEffectiveServiceTypesForFetch();
    fetchPolygonServiceRecordnBasedOnCountryId(
      countryName,
      serviceTypeFilterList,
      appStateObj.page,
      appFilterPopUpObj,
      false,
      appStateObj.currentTime,
      appStateObj.day
    );
  };

  const fetchPolygonServiceRecordnBasedOnCountryId = (countryName: any, serviceTypeProp: any, page: any, popUpObj: any, searchBy: any, currentTime: any, currentDay: string) => {
    let serviceTypeFilter = serviceTypeProp;
    if (isEmptyArray(serviceTypeProp)) {
      serviceTypeFilter = [...PlolygonServicesConstants.ALL_SERVICE_TYPE];
    }
    const request_payload = {
      countryName: countryName,
      data: {
        serviceTypes: [...serviceTypeFilter],
        selectedTile: "ALL",
        pageNo: page,
        pageSize: appStateObj.size,
        sort: "polygonId"
      },
    } as any;
    if (searchBy !== undefined && searchBy === true) {
      request_payload.data.searchBy = appStateObj.searchTypeValue;
      request_payload.data.searchTerm = appStateObj.searchValue;
    }
    if (!isEmptyOrNull(currentDay)) {
      request_payload.data.day = currentDay;
    }
    if (!isEmptyOrNull(currentTime) && isDateValid(currentTime)) {
      request_payload.data.filterTime = getMomentDateBasedOnFormat(currentTime, DateFormat.HH_MM_SS);
    }
    let serviceTypeFilterPayload: any = PlolygonServicesUtils.design_service_type_filter_from_header(popUpObj);
    if (!isUndefined(serviceTypeFilterPayload) && isArrayValid(serviceTypeFilterPayload)) {
      request_payload.data.serviceTypeFilterV2 = [...serviceTypeFilterPayload];
    }
    appDispatch(PlolygonServicesSlice.getPolygonServiceRecordnBasedOnCountryId(request_payload));
  };

  // Error
  //
  const configurePolygonServiceError = () => {
    if (polygonServiceConfigurationRecord !== undefined && polygonServiceConfigurationRecord.error !== undefined && polygonServiceConfigurationRecord.error !== "") {
      let newAppStateObj = PlolygonServicesUtils.handle_reset_pos_zone_mappind_details_utils(appStateObj, false, "", "", "", "");
      setAppStateObj(newAppStateObj);
      if (polygonServiceConfigurationRecord.error !== "" && typeof polygonServiceConfigurationRecord.error === "string" && !polygonServiceConfigurationRecord.error.includes(PlolygonServicesConstants.NO_POLYGON_EXIST_FOR_COUNTRY)) {
        CustomAlert(ALERT_TYPES.ERROR, polygonServiceConfigurationRecord.error);
      }
    }
  };

  // Data
  const configurePolygonServiceRecordListData = () => {
    if (
      polygonServiceConfigurationRecord !== undefined &&
      polygonServiceConfigurationRecord.fetchStatus !== undefined &&
      polygonServiceConfigurationRecord.fetchStatus === API_RESPONSE_STATUS.SUCCEEDED &&
      polygonServiceConfigurationRecord.data !== undefined &&
      typeof polygonServiceConfigurationRecord.data === "object"
    ) {
      let newAppStateObj = PlolygonServicesUtils.design_polygon_service_list_record(appStateObj, polygonServiceConfigurationRecord.data, servicePropositionList);
      setAppStateObj(newAppStateObj);
      // Read straight from the slice: requestedServiceTypes was stamped in the same reducer update as
      // .data, so it's always the service types that produced THIS data, even if a newer/older request
      // (e.g. the Map/List view toggle, which isn't gated by appStateObj.loading) resolves out of order.
      setAppCountStateObj({ ...appCountStateObj, appliedServiceTypes: polygonServiceConfigurationRecord.requestedServiceTypes || [] });
    }
  };

  const fetchNextRecord = (event: any, page: any) => {
    let is_search_based = false;
    if (!isUndefined(appStateObj) && !isEmptyOrNull(appStateObj.searchTypeValue) && !isEmptyOrNull(appStateObj.searchValue)) {
      is_search_based = true;
    }
    const pageNumber = Number(page) + 1;
    setAppStateObj({ ...appStateObj, loading: true, page: pageNumber });
    const serviceTypeFilter = getEffectiveServiceTypesForFetch();
    fetchPolygonServiceRecordnBasedOnCountryId(currentCountry.name, serviceTypeFilter, pageNumber, appFilterPopUpObj, is_search_based, appStateObj.currentTime, appStateObj.day);
  };

  const design_mapped_pos_zone_record = (listData: any, appFilterPopUpObjCopy: any) => {
    if (listData !== undefined && Array.isArray(listData)) {
      let newAppFilterPopUpObj = PlolygonServicesUtils._handle_mapped_pos_zone_list_by_service_utils(appFilterPopUpObjCopy, listData);
      setAppFilterPopUpObj(newAppFilterPopUpObj);
    }
  };

  const fetchMappedPosZoneListBasedOnServiceTypeAndCountry = async (payloadObject: any, newAppFilterPopUpObj: any) => {
    const { payload }: any = await appDispatch(PlolygonServicesSlice.getMappedPosZoneListBasedOnServiceTypeAndCountry(payloadObject));
    if (payload !== undefined && payload.success !== undefined && payload.success === true && payload.data !== undefined) {
      design_mapped_pos_zone_record(payload.data, newAppFilterPopUpObj);
    } else {
      if (payload !== undefined && typeof payload === "object" && payload.message !== undefined && typeof payload.message === "string" && payload.message !== "") {
        CustomAlert(ALERT_TYPES.ERROR, payload.message);
      } else {
        CustomAlert(ALERT_TYPES.ERROR, PlolygonServicesConstants.ERROR_OCCURRED_WHILE_SAVING);
      }
      let newAppFilterPopUpObj = PlolygonServicesUtils.handle_open_filter_pop_up_utils(undefined, "", appFilterPopUpObj, false);
      setAppFilterPopUpObj(newAppFilterPopUpObj);
    }
  };

  const handleFilterPopupOpen = (event: any, serviceType: any) => {
    if (serviceType !== undefined && serviceType !== "") {
      if (currentCountry !== undefined && currentCountry !== "" && currentCountry.name !== undefined && currentCountry.name !== "" && serviceType !== undefined && serviceType !== "") {
        let payloadObject = { countryName: currentCountry.name, serviceType: serviceType.toUpperCase() } as any;
        let newAppFilterPopUpObj = PlolygonServicesUtils.handle_open_filter_pop_up_utils(event, serviceType, appFilterPopUpObj, true);
        setAppFilterPopUpObj(newAppFilterPopUpObj);
        fetchMappedPosZoneListBasedOnServiceTypeAndCountry(payloadObject, newAppFilterPopUpObj);
      }
    }
  };

  const handleFilterPopupClose = (serviceType: any) => {
    let newAppFilterPopUpObj = PlolygonServicesUtils.handle_open_filter_pop_up_utils(undefined, "", appFilterPopUpObj, false);
    setAppFilterPopUpObj(newAppFilterPopUpObj);
  };

  const headerRowCheckboxOnChange = (event: any, listIndex: any, serviceTypeName: any) => {
    if (listIndex !== undefined && listIndex >= 0) {
      if (event !== undefined && event.target !== undefined && event.target.checked !== undefined) {
        const { checked } = event.target;
        if (
          appFilterPopUpObj !== undefined &&
          appFilterPopUpObj.posZoneServiceFilteredRecord !== undefined &&
          Array.isArray(appFilterPopUpObj.posZoneServiceFilteredRecord) &&
          appFilterPopUpObj.posZoneServiceFilteredRecord.length > 0 &&
          appFilterPopUpObj.posZoneServiceFilteredRecord[listIndex] !== undefined
        ) {
          let newAppFilterPopUpObj = PlolygonServicesUtils.handle_check_box_click_utils(appFilterPopUpObj, checked, listIndex);
          setAppFilterPopUpObj(newAppFilterPopUpObj);
        }
      }
    }
  };

  const handlePosZoneSearchTextChange = (event: any) => {
    if (event !== undefined && event.target !== undefined) {
      const { value } = event.target;
      let { listData } = PlolygonServicesUtils.design_pos_zone_based_on_search(appFilterPopUpObj.posZoneServiceRecord, value, appFilterPopUpObj.posZoneServiceFilteredRecord);
      setAppFilterPopUpObj({ ...appFilterPopUpObj, posZoneServiceFilteredRecord: listData, posZoneSearchText: value });
    }
  };

  const handlePosZoneSearch = () => { };

  const handleResetPopUpOnClick = (serviceType: any) => {
    let newAppFilterPopUpObj = PlolygonServicesUtils.handle_reset_pos_zone_filter_pop_up_utils(appFilterPopUpObj, serviceType);
    setAppFilterPopUpObj(newAppFilterPopUpObj);
  };

  const resetServiceTypeFilterAndTileAndFetchPolygonRecord = () => {
    setAppCountStateObj({ ...appCountStateObj, serviceTypeFilter: [] });
    setAppStateObj({ ...appStateObj, loading: true });
    setAppFilterPopUpObj({ ...appFilterPopUpObj, loading: false, openPopUp: false, anchorEl: null, posZoneSearchText: "", posZoneServiceRecord: [], posZoneServiceFilteredRecord: [] });
    fetchPolygonServiceRecordnBasedOnCountryId(currentCountry.name, getEffectiveServiceTypesForFetch(), 1, appFilterPopUpObj, false, appStateObj.currentTime, appStateObj.day);
  };

  const handleApplyPopUpOnClick = (serviceType: any) => {
    if (!isUndefined(serviceType) && !isEmptyOrNull(serviceType)) {
      resetServiceTypeFilterAndTileAndFetchPolygonRecord();
    }
  };

  const clearAllFilter = () => {
    setAppCountStateObj({ ...appCountStateObj, serviceTypeFilter: [] });
    const dayName = getTodaysDayInString();
    const hour = new Date().setHours(new Date().getHours());
    setAppStateObj({
      ...appStateObj,
      loading: true,
      searchValue: "",
      searchTypeValue: "polygonId",
      latSearchValue: "",
      lngSearchValue: "",
      currentTime: hour,
      day: dayName
    });
    let newAppFilterPopUpObj = PlolygonServicesUtils.handle_clear_all_pos_zone_filter_pop_up_utils(appFilterPopUpObj);
    setAppFilterPopUpObj(newAppFilterPopUpObj);
    fetchPolygonServiceRecordnBasedOnCountryId(currentCountry.name, getEffectiveServiceTypesForFetch(), 1, newAppFilterPopUpObj, false, hour, dayName);
  };

  const handleSearchTypeChange = (selected: any) => {
    if (selected !== undefined && selected !== "") {
      setAppStateObj({ ...appStateObj, searchTypeValue: selected, latSearchValue: "", lngSearchValue: "" });
    }
  };

  const handleSearchBoxTextChange = (event: any) => {
    if (event?.target) {
      const { value, name } = event.target;
      if (name === "latSearchValue" || name === "lngSearchValue") {
        if (value === "") {
          setAppStateObj({ ...appStateObj, [name]: value });
        } else if (name === "latSearchValue" && value > -89 && value < 91) {
          setAppStateObj({ ...appStateObj, [name]: String(value) });
        } else if (name === "lngSearchValue" && value > -179 && value < 181) {
          setAppStateObj({ ...appStateObj, [name]: String(value) });
        }
      } else {
        setAppStateObj({ ...appStateObj, searchValue: value });
      }
    }
  };

  const handleSearchIconClick = () => {
    if (appStateObj !== undefined && appStateObj.searchTypeValue !== undefined && appStateObj.searchTypeValue !== "") {
      if (appStateObj !== undefined && appStateObj.searchValue !== undefined && appStateObj.searchValue !== "") {
        setAppStateObj({ ...appStateObj, loading: true });
        if (appStateObj.polygonView === PlolygonServicesConstants.POLYGON_VIEW_TYPE.MAP) {
          showSearchedPolygonOnMap();
        } else {
          setAppCountStateObj({ ...appCountStateObj, serviceTypeFilter: [] });
          let newAppFilterPopUpObj = PlolygonServicesUtils.handle_clear_all_pos_zone_filter_pop_up_utils(appFilterPopUpObj);
          setAppFilterPopUpObj(newAppFilterPopUpObj);
          fetchPolygonServiceRecordnBasedOnCountryId(currentCountry.name, getEffectiveServiceTypesForFetch(), 1, newAppFilterPopUpObj, true, appStateObj.currentTime, appStateObj.day);
        }
      } else {
        CustomAlert(ALERT_TYPES.ERROR, "Please provide search value");
      }
    } else {
      CustomAlert(ALERT_TYPES.ERROR, "Please provide search type");
    }
  };

  const handleSearchBoxKeyDownChange = (event: any) => {
    if ((event.key !== undefined && event.key === "Enter") || event.keyCode === 13) {
      handleSearchIconClick();
    }
  };

  const fetch_polygon_details_based_on_id_service_type_and_country = async (polygonId: any, polygonName: any, countryName: any, serviceType: any) => {
    if (polygonId !== undefined && polygonId !== "" && countryName !== undefined && countryName !== "" && serviceType !== undefined && serviceType !== "") {
      let newPolygonDetailsSideDrawerObj = PlolygonServicesUtils.reset_polygon_details_history_drawer_obj(polygonDetailsSideDrawerObj);
      setPolygonDetailsSideDrawerObj(newPolygonDetailsSideDrawerObj);
      //
      let payloadObject = { polygonId: polygonId, serviceType: serviceType, country: countryName } as any;
      const { payload }: any = await appDispatch(PlolygonServicesSlice.getPolygonDetailsHistoryBasedOnIdServiceTypeAndCountry(payloadObject));
      if (payload !== undefined && payload.success !== undefined && payload.success === true && payload.data !== undefined) {
        let updatedPolygonDetailsSideDrawerObj = PlolygonServicesUtils.design_polygon_history_record_utils(polygonId, polygonName, serviceType, payload.data, newPolygonDetailsSideDrawerObj);
        setPolygonDetailsSideDrawerObj(updatedPolygonDetailsSideDrawerObj);
      } else {
        if (payload !== undefined && typeof payload === "object" && payload.message !== undefined && typeof payload.message === "string" && payload.message !== "") {
          CustomAlert(ALERT_TYPES.ERROR, payload.message);
        } else {
          CustomAlert(ALERT_TYPES.ERROR, PlolygonServicesConstants.ERROR_OCCURRED_WHILE_SAVING);
        }
        hanlePolygonDetailsDrawerClose();
      }
    }
  };

  const hanlePolygonDetailsDrawerClose = () => {
    let newPolygonDetailsSideDrawerObj = PlolygonServicesUtils.close_polygon_details_history_drawer_obj(polygonDetailsSideDrawerObj);
    setPolygonDetailsSideDrawerObj(newPolygonDetailsSideDrawerObj);
    setPolygonDetailsSideDrawerObj({ ...polygonDetailsSideDrawerObj, openDrawer: false, loading: false, polygonId: "", polygonName: "", polygonDetailsHistory: [] });
  };

  const handleExportCSVClick = async () => {
    if (!isEmptyOrNull(currentCountry?.name)) {
      setAppStateObj({ ...appStateObj, isExporting: true });
      let payload_request = { countryName: currentCountry.name };
      const { payload }: any = await appDispatch(PlolygonServicesSlice.exportToExcel(payload_request)); // NOSONAR
      setAppStateObj({ ...appStateObj, isExporting: false });
      if (!isEmptyOrNull(payload?.fileName)) {
        downloadXLSXFile(payload.fileName, payload?.response);
      } else {
        let error_message = "Something went wrong, Please try again later";
        if (!isEmptyOrNull(payload?.message)) {
          error_message = payload.message;
        }
        CustomAlert(ALERT_TYPES.ERROR, error_message);
      }
    }
  };

  const handlePolygonClick = (event: any, polygon: PlolygonServicesInterface.MapPolygonInterface) => {
    if ( !isEmptyOrNull(polygon?.polygonId)) {
      handlePolygonDetailsClick(polygon.polygonId);
    } else {
      CustomAlert(ALERT_TYPES.ERROR, "Please clear the POS level filter to view polygon details");
      return;
    }
  };

  const showSearchedPolygonOnMap = async () => {
    let searchedIndex =
      PlolygonServicesUtils.get_searched_polygon__index_utils(appStateObj);
    if (searchedIndex !== -1) {
      const foundPolygon = appStateObj.mapPolygonsRecord[searchedIndex];
      
      // Fetch polygon details (mimicking handlePolygonDetailsClick)
      const polygonId = foundPolygon.polygonId;
      if (!isEmptyOrNull(polygonId)) {
        setAppStateObj({ ...appStateObj, overlayLoader: true });
        const { payload }: any = await appDispatch(PlolygonServicesSlice.fetchPolygonIdDetails(polygonId));
        if (!isEmptyOrNull(payload?.polygonId)) {
          // Design polygon details data (same as handlePolygonDetailsClick)
          const polygonDetailsPopUpData = PlolygonServicesUtils.designServiceTypeRecordListUtils(payload, servicePropositionList, userInfo);
          
          // Base update with openPolygonDetailsPopUp set to false (key difference)
          const baseUpdate = {
            openPolygonDetailsPopUp: false,
            polygonDetailsPopUpData: { polygonId, serviceTypeRecordList: polygonDetailsPopUpData },
            loading: false,
            overlayLoader: false,
          };
          
          // Highlight the polygon exactly like handlePolygonClickForDetailsPopUp
          if (appStateObj?.polygonView === PlolygonServicesConstants.POLYGON_VIEW_TYPE.MAP && !appStateObj.isPosLevelFilteredApplied) {
            const updatedPolygons = getUpdatedPolygons(appStateObj.mapPolygonsRecord, (polygon: PlolygonServicesInterface.MapPolygonInterface) => {
              // Highlight the searched polygon
              if (polygon.polygonId === polygonId) {
                return {
                  ...polygon,
                  polygonKey: `${polygonId}-${new Date().getTime()}`,
                  options: {
                    ...polygon.options,
                    fillOpacity: 1,
                    fillColor: polygon.options.strokeColorCopy,
                    strokeColor: polygon.options.fillColorCopy,
                  },
                };
              } 
              // Reset any previously highlighted polygon
              else if (polygon.options.fillOpacity === 1) {
                return {
                  ...polygon,
                  polygonKey: `${polygon.polygonId}-${new Date().getTime()}`,
                  options: {
                    ...polygon.options,
                    fillOpacity: 0.6,
                    fillColor: polygon.options.fillColorCopy,
                    strokeColor: polygon.options.strokeColorCopy,
                  },
                };
              }
              else {
                return polygon;
              }
            });
            setAppStateObj({ ...appStateObj, ...baseUpdate, mapPolygonsRecord: updatedPolygons });
          } else {
            setAppStateObj({ ...appStateObj, ...baseUpdate });
          }
        } else {
          setAppStateObj({ ...appStateObj, loading: false, overlayLoader: false });
          let errorMessage = PlolygonServicesConstants.POLYGON_CONSTANT_TEXT.ERROR_FETCHING_CONFIG;
          if (payload.message && payload.message !== PlolygonServicesConstants.POLYGON_CONSTANT_TEXT.NO_MESSAGE_AVAILABLE) {
            errorMessage = payload.message;
          }
          CustomAlert(ALERT_TYPES.ERROR, errorMessage);
        }
      }
    } else {
      setAppStateObj({ ...appStateObj, loading: false });
    }
  };

  const clearAllMapFilter = () => {
    setAppCountStateObj({ ...appCountStateObj, serviceTypeFilter: [] });
    setAppStateObj({ ...appStateObj, overlayLoader: true, mapPolygonsRecord: [], mapMarkersRecord: [], searchValue: "", searchTypeValue: "polygonId" });
    const polygonRecord = PlolygonServicesUtils.design_polygon_map_view_record_utils(polygonServiceMapRecord.data, appStateObj.serviceBasedColorRecord);
    const mapMarkersRecord = PlolygonServicesUtils.re_enter_map_marker_data(appStateObj);
    let center = { lat: appStateObj.mapDefaultCenter.lat, lng: appStateObj.mapDefaultCenter.lng };
    if (!isUndefined(appStateObj) && !isUndefined(appStateObj?.latSearchValue) && !isUndefined(appStateObj?.lngSearchValue)) {
      if (countryDetails?.data?.serviceTypes && isArrayValid(countryDetails.data.serviceTypes)) {
        let obj = countryDetails.data.serviceTypes.find((service: any) => service?.defaultPos?.latitude);
        if (obj?.defaultPos?.longitude) {
          center.lat = Number(obj.defaultPos.latitude);
          center.lng = Number(obj.defaultPos.longitude);
        }
      }
    }
    setAppStateObj({
      ...appStateObj,
      overlayLoader: false,
      mapPolygonsRecord: polygonRecord,
      mapMarkersRecord: mapMarkersRecord,
      mapDefaultCenter: center,
      mapCenter: center,
      searchValue: "",
      searchTypeValue: "polygonId",
      isPosLevelFilteredApplied: false,
      posLevelFilterData: null,
      latSearchValue: "",
      lngSearchValue: "",
      currentTime: new Date().setHours(new Date().getHours()),
      day: getTodaysDayInString()
    });
  };

  // Listing view: multi-select service types. Toggling a checkbox only updates the checkbox/dropdown
  // state (appCountStateObj.selectedServiceTypes); columns keep showing appliedServiceTypes (the last
  // successfully fetched selection) until the dropdown-close refetch below resolves.
  const handleServiceTypeSelectionChange = (selectedIds: Array<string>) => {
    // Enforce at least one selected column
    if (!isArrayValid(selectedIds)) {
      return;
    }
    selectedServiceTypesRef.current = selectedIds;
    setAppCountStateObj({ ...appCountStateObj, selectedServiceTypes: selectedIds });
  };

  // Fires once when the Listing-view service-type dropdown closes, regardless of how many
  // checkboxes were toggled during that open session. Reads the ref (not appCountStateObj) so it
  // always uses the final selection at close time.
  const handleServiceTypeSelectionDropdownClose = () => {
    setAppStateObj({ ...appStateObj, loading: true });
    fetchPolygonServiceRecordnBasedOnCountryId(currentCountry.name, selectedServiceTypesRef.current, 1, appFilterPopUpObj, false, appStateObj.currentTime, appStateObj.day);
  };

  // Map view: checkbox-style dropdown (MultiSelectBox) but restricted to exactly one selection.
  // MultiSelectBox always appends the just-clicked item to the end of the array, so collapsing
  // to that last entry both enforces single-select and swaps to whichever item the user just checked.
  const handleMapServiceTypeSelectionChange = async (selectedIds: Array<string>) => {
    // Enforce at least one selected service type (ignore attempts to uncheck the only selection)
    if (!isArrayValid(selectedIds)) {
      return;
    }
    const value = selectedIds[selectedIds.length - 1];
    const serviceTypeFilter = [value];

    setAppStateObj({ ...appStateObj, overlayLoader: true });
    const request_payload = { serviceTypes: serviceTypeFilter, status: [SLOT_STATUSES.ACTIVATED] };
    const { payload }: any = await appDispatch(PlolygonServicesSlice.getPosZoneRecordPositionDetails(request_payload)); // NOSONAR
    if (isArrayValid(payload)) {
      setAppCountStateObj({ ...appCountStateObj, serviceTypeFilter });
      const markersRecord = PlolygonServicesUtils.design_polygon_map_marker_record_utils(appStateObj, payload, servicePropositionList, serviceTypeFilter);
      let updatedAppStateObj = PlolygonServicesUtils.handle_single_service_type_change_utils({ ...appStateObj, mapMarkersRecord: markersRecord }, serviceTypeFilter);
      setAppStateObj(updatedAppStateObj);
    } else {
      setAppStateObj({ ...appStateObj, overlayLoader: false });
      let errorMessage = PlolygonServicesConstants.ERROR_OCCURRED_WHILE_SAVING;
      if (!isEmptyOrNull(payload?.message) && isAString(payload?.message)) {
        errorMessage = payload.message;
      }
      CustomAlert(ALERT_TYPES.ERROR, errorMessage);
    }
  };

  const openPolygonPosZoneInfoWindow = (markerIndex: number) => {
    if (appStateObj?.mapMarkersRecord?.[markerIndex]?.name) {
      const newAppStateObj = PlolygonServicesUtils.turn_on_overlay_loader(appStateObj);
      setAppStateObj(newAppStateObj);
      let updatedAppStateObj = PlolygonServicesUtils.open_polygon_pos_zone_info_window_utils(markerIndex, newAppStateObj, appCountStateObj);
      setAppStateObj(updatedAppStateObj);
    }
  };

  const closePolygonPosZoneInfoWindow = (event: any) => {
    event.stopPropagation();
    let newAppStateObj = PlolygonServicesUtils.close_polygon_pos_zone_info_window_utils(appStateObj);
    setAppStateObj(newAppStateObj);
  };

  const selectServiceTypeClick = (marker: PlolygonServicesInterface.MAP_MARKER_INTERFACE, serviceIndex: number) => {
    setAppStateObj({ ...appStateObj, overlayLoader: true });
    let newAppStateObj = PlolygonServicesUtils.select_service_polygon_pos_zone_info_window_utils(marker, serviceIndex, polygonServiceMapRecord.data, appStateObj);
    setAppStateObj(newAppStateObj);
  };

  const selectServiceZoneTypeClick = (marker: any, zone: any, zoneIndex: number) => {
    setAppStateObj({ ...appStateObj, overlayLoader: true, mapPolygonsRecord: [] });
    let newAppStateObj = PlolygonServicesUtils.select_service_zone_polygon_pos_zone_info_window_utils(marker, zone, zoneIndex, polygonServiceMapRecord.data, appStateObj);
    setAppStateObj(newAppStateObj);
  };

  const clearPosLevelFilter = () => {
    const polygonRecord = PlolygonServicesUtils.design_polygon_map_view_record_utils(polygonServiceMapRecord.data, appStateObj.serviceBasedColorRecord, appCountStateObj.serviceTypeFilter);
    const newMapMarkersRecord = PlolygonServicesUtils.reset_map_marker_data(appStateObj);
    setAppStateObj({
      ...appStateObj,
      overlayLoader: false,
      mapPolygonsRecord: polygonRecord,
      mapMarkersRecord: newMapMarkersRecord,
      isPosLevelFilteredApplied: false,
      posLevelFilterData: null,
    });
  };

  const designSuccessPolygonServiceMapRecord = () => {
    if (!isEmptyOrNull(polygonServiceMapRecord?.data)) {
      const polygonRecord = PlolygonServicesUtils.design_polygon_map_view_record_utils(polygonServiceMapRecord.data, appStateObj.serviceBasedColorRecord, appCountStateObj.serviceTypeFilter, appStateObj);
      const newMapMarkersRecord = PlolygonServicesUtils.refresh_map_marker_data(appStateObj);
      setAppStateObj({
        ...appStateObj,
        overlayLoader: false,
        mapPolygonsRecord: polygonRecord,
        mapMarkersRecord: newMapMarkersRecord,
        openPolygonDetailsPopUp: false,
        polygonDetailsPopUpData: {
          polygonId: "",
          serviceTypeRecordList: []
        },
      });
    } else {
      setAppStateObj({ ...appStateObj, overlayLoader: false });
    }
  };

  const designPolygonServiceMapRecord = () => {
    if (!isEmptyOrNull(polygonServiceMapRecord?.error)) {
      setAppStateObj({ ...appStateObj, overlayLoader: false });
    } else {
      designSuccessPolygonServiceMapRecord();
    }
  };

  /** Once map is loaded on the screen --> onIdle is called with boundries */
  const fetchPolygonServiceMapRecordnBasedOnBoundaries = (northEast: CoordinatesInterface, southWest: CoordinatesInterface) => {
    const currentTime: any = new Date().setHours(new Date().getHours());
    let filterTime: any = moment(currentTime).format("HH:mm:ss");
    if (moment(appStateObj.currentTime).isValid()) {
      filterTime = moment(appStateObj.currentTime).format("HH:mm:ss");
    }
    const request_payload = {
      boundaries: { northEast: `${northEast.lat},${northEast.lng}`, southWest: `${southWest.lat},${southWest.lng}` },
      filterTime: filterTime,
      day: !isEmptyOrNull(appStateObj.day) ? appStateObj.day : getTodaysDayInString(),
    };
    setAppStateObj({ ...appStateObj, overlayLoader: true, boundaries: request_payload.boundaries });
    appDispatch(PlolygonServicesSlice.getPolygonServiceMapRecordnBasedOnBoundaries(request_payload));
  };

  /** If pos zone data present then create marker and obtain center. And Show map.
   *  Once map loaded on to the screen onIdle is called by default
   *  */
  const showMapAndMoveCenterBasedOnCountry = (markersRecord: any) => {
    let center = { lat: 0, lng: 0 };
    if (isArrayValid(countryDetails?.data?.serviceTypes)) {
      let obj = countryDetails.data.serviceTypes.find((service: any) => service?.defaultPos?.latitude);
      if (!isEmptyOrNull(obj?.defaultPos?.longitude)) {
        center.lat = Number(obj.defaultPos.latitude);
        center.lng = Number(obj.defaultPos.longitude);
      }
    }
    setAppStateObj({
      ...appStateObj,
      loading: false,
      polygonView: PlolygonServicesConstants.POLYGON_VIEW_TYPE.MAP,
      overlayLoader: true,
      mapDefaultCenter: center,
      mapCenter: center,
      mapMarkersRecord: markersRecord,
      mapPolygonsRecord: [],
      currentTime: new Date().setHours(new Date().getHours()),
      boundaries: null,
      polygonServiceListData: [],
      page: 1,
      size: 50,
      maxRecordPresent: 0,
      polygonServiceListHeader: {},
      serviceBasedColorRecord: PlolygonServicesUtils.getServiceTypeColorList(servicePropositionList),
    });
  };

  /** To switch to map view first fetch pos of the country to create markers and get default pos lat lng to add map center */
  const fetchPosZoneRecordPositionDetails = async (serviceTypeRecord: Array<string>) => {
    setAppStateObj({ ...appStateObj, loading: true, searchValue: "", searchTypeValue: "polygonId", currentTime: new Date().setHours(new Date().getHours()), boundaries: null });
    const request_payload = { serviceTypes: serviceTypeRecord, status: [SLOT_STATUSES.ACTIVATED] };
    const { payload }: any = await appDispatch(PlolygonServicesSlice.getPosZoneRecordPositionDetails(request_payload)); // NOSONAR
    if (isArrayValid(payload)) {
      setAppCountStateObj({ ...appCountStateObj, serviceTypeFilter: serviceTypeRecord });
      const markersRecord = PlolygonServicesUtils.design_polygon_map_marker_record_utils(appStateObj, payload, servicePropositionList, serviceTypeRecord);
      showMapAndMoveCenterBasedOnCountry(markersRecord);
    } else {
      setAppStateObj({ ...appStateObj, loading: false });
      let errorMessage = PlolygonServicesConstants.ERROR_OCCURRED_WHILE_SAVING;
      if (!isEmptyOrNull(payload?.message) && isAString(payload?.message)) {
        errorMessage = payload.message;
      }
      CustomAlert(ALERT_TYPES.ERROR, errorMessage);
    }
  };

  const switchPolygonView = () => {
    switch (appStateObj.polygonView) {
      case PlolygonServicesConstants.POLYGON_VIEW_TYPE.LISTING: {
        // Default to first/primary service type only for initial map load
        const defaultServiceType = PlolygonServicesUtils.getDefaultServiceTypeForMap(servicePropositionList, userInfo);
        const serviceTypeRecord = defaultServiceType ? [defaultServiceType] : PlolygonServicesUtils.generateServiceNameForPolygon(servicePropositionList, userInfo);
        setAppCountStateObj({ ...appCountStateObj, serviceTypeFilter: serviceTypeRecord });
        fetchPosZoneRecordPositionDetails(serviceTypeRecord);
        return;
      }
      case PlolygonServicesConstants.POLYGON_VIEW_TYPE.MAP:
        setAppStateObj({
          ...appStateObj,
          polygonView: PlolygonServicesConstants.POLYGON_VIEW_TYPE.LISTING,
          loading: true,
          mapMarkersRecord: [],
          mapPolygonsRecord: [],
          searchValue: "",
          searchTypeValue: "polygonId",
          isPosLevelFilteredApplied: false,
          posLevelFilterData: null,
          latSearchValue: "",
          lngSearchValue: "",
          currentTime: new Date().setHours(new Date().getHours()),
          boundaries: null,
          serviceBasedColorRecord: [],
          openPolygonDetailsPopUp: false,
          polygonDetailsPopUpData: {
            polygonId: "",
            serviceTypeRecordList: []
          },
        });
        fetchPolygonRecord(currentCountry.name);
        return;
    }
  };

  const posZoneMarkerInfoWindow = (marker: any) => {
    return <PolygonPosZoneInfoWindowViewUtils classes={classes} marker={marker} selectServiceTypeClick={selectServiceTypeClick} closePolygonPosZoneInfoWindow={closePolygonPosZoneInfoWindow} selectServiceZoneTypeClick={selectServiceZoneTypeClick} />;
  };

  const moveMapToCordinates = () => {
    if (!isEmptyOrNull(appStateObj?.latSearchValue) && !isEmptyOrNull(appStateObj?.lngSearchValue)) {
      const isLatitude = PlolygonServicesUtils.isLatitudeValid(appStateObj.latSearchValue);
      const isLongitude = PlolygonServicesUtils.isLongitudeValid(appStateObj.lngSearchValue);
      if (isLatitude && isLongitude) {
        const newAppStateObj = PlolygonServicesUtils.move_map_to_cordinates_on_search_utils(appStateObj);
        setAppStateObj(newAppStateObj);
      } else {
        CustomAlert(ALERT_TYPES.ERROR, PlolygonServicesConstants.LATITUDE_LONGITUDE_ERROR);
      }
    } else {
      CustomAlert(ALERT_TYPES.ERROR, PlolygonServicesConstants.LATITUDE_LONGITUDE_EMPTY_ERROR);
    }
  };

  const handleApplyFilter = (updatedState: PlolygonServicesInterface.PlolygonServicesInitialStateInteface) => {
    if (updatedState.polygonView === PlolygonServicesConstants.POLYGON_VIEW_TYPE.LISTING) {
      const { currentTime, day } = updatedState;
      setAppStateObj({ ...updatedState, loading: true });
      const serviceTypeFilterList = getEffectiveServiceTypesForFetch();
      fetchPolygonServiceRecordnBasedOnCountryId(
        currentCountry.name,
        serviceTypeFilterList,
        1,
        appFilterPopUpObj,
        false,
        currentTime,
        day
      );
    }

    if (updatedState.polygonView === PlolygonServicesConstants.POLYGON_VIEW_TYPE.MAP) {
      if (!isEmptyOrNull(updatedState?.boundaries)) {
        const request_payload = {
          boundaries: updatedState.boundaries,
          filterTime: getMomentDateBasedOnFormat(updatedState.currentTime, DateFormat.HH_MM_SS),
          day: updatedState.day,
        };
        setAppStateObj({ ...updatedState, overlayLoader: true, mapPolygonsRecord: [] });
        appDispatch(PlolygonServicesSlice.getPolygonServiceMapRecordnBasedOnBoundaries(request_payload));
      }
    }
  };

  const handleServiceTimeChange = (name: string, data: any) => {
    let updatedState = { ...appStateObj, [name]: data };
    switch (name) {
      case PlolygonServicesConstants.POLYGON_CONSTANT_TEXT.TIME_FILTER:
        if (isDateValid(data)) {
          handleApplyFilter(updatedState);
        }
        break;

      case PlolygonServicesConstants.POLYGON_CONSTANT_TEXT.DAY_FILTER:
        const { value } = data.target || "";
        if (!isEmptyOrNull(value)) {
          updatedState = { ...appStateObj, [name]: value };
          handleApplyFilter(updatedState);
        }
        break;

      default:
        break;
    }
  };

  const handlePolygonDetailsClick = async (polygonId: string, serviceType?: string) => {
    setAppStateObj({ ...appStateObj, overlayLoader: true });
    const { payload }: any = await appDispatch(PlolygonServicesSlice.fetchPolygonIdDetails(polygonId));
    if (!isEmptyOrNull(payload?.polygonId)) {
      handlePolygonClickForDetailsPopUp(
        payload.polygonId,
        PlolygonServicesUtils.designServiceTypeRecordListUtils(payload, servicePropositionList, userInfo),
        serviceType
      );
    } else {
      setAppStateObj({ ...appStateObj, overlayLoader: false });
      let errorMessage = PlolygonServicesConstants.POLYGON_CONSTANT_TEXT.ERROR_FETCHING_CONFIG;
      if (payload.message && payload.message !== PlolygonServicesConstants.POLYGON_CONSTANT_TEXT.NO_MESSAGE_AVAILABLE) {
        errorMessage = payload.message;
      }
      CustomAlert(ALERT_TYPES.ERROR, errorMessage);
    }
  };

  const getUpdatedPolygons = (polygons: Array<PlolygonServicesInterface.MapPolygonInterface> | [], updateLogic: any) => {
    return polygons?.map(polygon => updateLogic(polygon));
  };

  const handlePolygonClickForDetailsPopUp = (polygonId: string, polygonDetailsPopUpData: PlolygonServicesInterface.ServiceTypeRecord[], serviceType?: string) => {
    const baseUpdate = {
      openPolygonDetailsPopUp: true,
      polygonDetailsPopUpData: { polygonId, serviceTypeRecordList: polygonDetailsPopUpData, preSelectedServiceType: serviceType },
      overlayLoader: false,
    };

    if (appStateObj?.polygonView === PlolygonServicesConstants.POLYGON_VIEW_TYPE.MAP && !appStateObj.isPosLevelFilteredApplied) {
      const updatedPolygons = getUpdatedPolygons(appStateObj.mapPolygonsRecord, (polygon: PlolygonServicesInterface.MapPolygonInterface) => {
        if (polygon.polygonId === polygonId) {
          return {
            ...polygon,
            polygonKey: `${polygonId}-${new Date().getTime()}`,
            options: {
              ...polygon.options,
              fillOpacity: 1,
              fillColor: polygon.options.strokeColorCopy,
              strokeColor: polygon.options.fillColorCopy,
            },
          };
        } else if (polygon.options.fillOpacity === 1) {
          return {
            ...polygon,
            polygonKey: `${polygon.polygonId}-${new Date().getTime()}`,
            options: {
              ...polygon.options,
              fillOpacity: 0.6,
              fillColor: polygon.options.fillColorCopy,
              strokeColor: polygon.options.strokeColorCopy,
            },
          };
        }
        else {
          return polygon;
        }
      });
      setAppStateObj({ ...appStateObj, ...baseUpdate, mapPolygonsRecord: updatedPolygons });
    }
    else {
      setAppStateObj({ ...appStateObj, ...baseUpdate, });
    }
  };

  const handleClosePolygonDetailsPopUp = () => {
    const baseUpdate = {
      openPolygonDetailsPopUp: false,
      polygonDetailsPopUpData: { polygonId: "", serviceTypeRecordList: [], preSelectedServiceType: undefined },
      overlayLoader: false,
    };

    if (appStateObj?.polygonView === PlolygonServicesConstants.POLYGON_VIEW_TYPE.MAP && !appStateObj.isPosLevelFilteredApplied) {
      const updatedPolygons = getUpdatedPolygons(appStateObj.mapPolygonsRecord, (polygon: PlolygonServicesInterface.MapPolygonInterface) => {
        return {
          ...polygon,
          polygonKey: `${polygon.polygonId}-${new Date().getTime()}`,
          options: {
            ...polygon.options,
            fillOpacity: 0.6,
            fillColor: polygon.options.fillColorCopy,
            strokeColor: polygon.options.strokeColorCopy,
          },
        };
      });
      setAppStateObj({ ...appStateObj, ...baseUpdate, mapPolygonsRecord: updatedPolygons, });
    } else {
      setAppStateObj({ ...appStateObj, ...baseUpdate, });
    }

    // if (appStateObj?.polygonView === PlolygonServicesConstants.POLYGON_VIEW_TYPE.MAP) {
    //   const updatedPolygons = appStateObj?.mapPolygonsRecord?.map((polygon) => {
    //     return {
    //       ...polygon,
    //       polygonKey: `${polygon.polygonId}-${new Date().getTime()}`,
    //       options: {
    //         ...polygon.options,
    //         fillOpacity: 0.6,
    //         fillColor: polygon.options.fillColorCopy,
    //         strokeColor: polygon.options.strokeColorCopy,
    //       },
    //     };
    //   });

    //   setAppStateObj({
    //     ...appStateObj,
    //     openPolygonDetailsPopUp: false,
    //     polygonDetailsPopUpData: {
    //       polygonId: "",
    //       serviceTypeRecordList: []
    //     },
    //     overlayLoader: false,
    //     mapPolygonsRecord: updatedPolygons
    //   });
    // } else {
    //   setAppStateObj({
    //     ...appStateObj,
    //     openPolygonDetailsPopUp: false,
    //     polygonDetailsPopUpData: {
    //       polygonId: "",
    //       serviceTypeRecordList: []
    //     },
    //     overlayLoader: false
    //   });
    // }
  };

  const renderPolygonMapService = () => {
    return appStateObj?.polygonView === PlolygonServicesConstants.POLYGON_VIEW_TYPE.MAP && (
      <PolygonMapService
        classes={classes}
        appStateObj={appStateObj}
        fetchPolygonServiceMapRecordnBasedOnBoundaries={fetchPolygonServiceMapRecordnBasedOnBoundaries}
        openPolygonPosZoneInfoWindow={openPolygonPosZoneInfoWindow}
        handlePolygonClick={handlePolygonClick}
        posZoneMarkerInfoWindow={posZoneMarkerInfoWindow}
      />
    );
  }

  useEffect(() => {
    polygonServiceMapRecord?.fetchStatus !== API_RESPONSE_STATUS.IDLE && polygonServiceMapRecord?.fetchStatus !== API_RESPONSE_STATUS.LOADING && designPolygonServiceMapRecord();
  }, [polygonServiceMapRecord]);

  useEffect(() => {
    !isEmptyOrNull(polygonServiceConfigurationRecord?.data) && configurePolygonServiceRecordListData();
  }, [polygonServiceConfigurationRecord, polygonServiceConfigurationRecord.data]);

  useEffect(() => {
    !isEmptyOrNull(polygonServiceConfigurationRecord?.error) && configurePolygonServiceError();
  }, [polygonServiceConfigurationRecord, polygonServiceConfigurationRecord.error]);

  useEffect(() => {
    if (!isUndefined(servicePropositionList) && !isEmptyOrNull(currentCountry?.name)) {
      setAppStateObj({ ...appStateObj, loading: true });
      fetchPolygonRecord(currentCountry.name);
    }
  }, [servicePropositionList, currentCountry]);

  useEffect(() => {
    if (!isUndefined(servicePropositionList) && isArrayValid(servicePropositionList)) {
      const serviceTypeList = PlolygonServicesUtils.generateServiceNameValueForPolygon(servicePropositionList, userInfo);
      // Default: first 3 service types (by rank) selected as visible columns
      const defaultSelectedServiceTypes = serviceTypeList.slice(0, 3).map((service: any) => service.id);
      setAppCountStateObj({ ...appCountStateObj, serviceTypeList, selectedServiceTypes: defaultSelectedServiceTypes });
    }
  }, [servicePropositionList]);

  useEffect(() => {
    return () => {
      appDispatch(PlolygonServicesSlice.resetPolygonServiceConfigurationServiceState());
      localStorage.removeItem(PlolygonServicesConstants.POLYGON_ID_LOCAL_STORAGE);
    };
  }, []);

  return (
    <>
      <div className={classes.container}>
        <OverlayLoader loading={appStateObj.overlayLoader} />
        <PlolygonServicesHeader
          classes={classes}
          appStateObj={appStateObj}
          isExporting={appStateObj.isExporting}
          handleExportCSVClick={handleExportCSVClick}
          switchPolygonView={switchPolygonView}
          userInfo={userInfo}
        />
        {appStateObj.loading ? (
          <div className={classes.loaderDiv}>
            <Loader />
          </div>
        ) : (
          <>
            <PolygonServiceFilterSection
              classes={classes}
              appStateObj={appStateObj}
              serviceTypeList={appCountStateObj.serviceTypeList}
              serviceTypeFilter={appCountStateObj.serviceTypeFilter}
              selectedServiceTypes={appCountStateObj.selectedServiceTypes}
              handleMapServiceTypeSelectionChange={handleMapServiceTypeSelectionChange}
              handleServiceTypeSelectionChange={handleServiceTypeSelectionChange}
              handleServiceTypeSelectionDropdownClose={handleServiceTypeSelectionDropdownClose}
              handleSearchTypeChange={handleSearchTypeChange}
              handleSearchBoxTextChange={handleSearchBoxTextChange}
              handleSearchBoxKeyDownChange={handleSearchBoxKeyDownChange}
              handleSearchIconClick={handleSearchIconClick}
              clearAllFilter={appStateObj.polygonView === PlolygonServicesConstants.POLYGON_VIEW_TYPE.LISTING ? clearAllFilter : clearAllMapFilter}
              clearPosLevelFilter={clearPosLevelFilter}
              moveMapToCordinates={moveMapToCordinates}
              handleServiceTimeChange={handleServiceTimeChange}
            />
            {appStateObj.polygonView === PlolygonServicesConstants.POLYGON_VIEW_TYPE.LISTING ? (
              <PlolygonServicesCardSection
                classes={classes}
                rowsData={appStateObj.polygonServiceListData}
                currentCountry={currentCountry}
                userInfo={userInfo}
                appStateObj={appStateObj}
                appliedServiceTypes={appCountStateObj.appliedServiceTypes}
                fetchNextRecord={fetchNextRecord}
                handleFilterPopupOpen={handleFilterPopupOpen}
                handleFilterPopupClose={handleFilterPopupClose}
                appFilterPopUpObj={appFilterPopUpObj}
                headerRowCheckboxOnChange={headerRowCheckboxOnChange}
                handlePosZoneSearch={handlePosZoneSearch}
                handlePosZoneSearchTextChange={handlePosZoneSearchTextChange}
                handleResetPopUpOnClick={handleResetPopUpOnClick}
                handleApplyPopUpOnClick={handleApplyPopUpOnClick}
                handlePolygonDetailsClick={handlePolygonDetailsClick}
              />
            ) : renderPolygonMapService()}
          </>
        )}
      </div>
      {appStateObj.openPolygonDetailsPopUp && (
        <RenderPolygonDetailsPopUp
          open={appStateObj.openPolygonDetailsPopUp}
          handleClose={handleClosePolygonDetailsPopUp}
          polygonDetailsPopUpData={appStateObj.polygonDetailsPopUpData}
          polygonServiceRecord={PlolygonServicesUtils.generateServiceNameValueForPolygon(servicePropositionList, userInfo)}
          showEditButton={PlolygonServicesUtils.allowPolygonServiceEdit(userInfo?.userProfile?.role)}
          isFromMapView={appStateObj?.polygonView === PlolygonServicesConstants.POLYGON_VIEW_TYPE.MAP}
        />
      )}
    </>
  );
};

export default React.memo(PlolygonServicesPage);
